/* =====================================================================
 * routing — DI-based workspace-mode ↔ URL-path resolver.
 *
 * Hoisted in P11-4g-pass2 so shared components (admin / billing /
 * plugin / settings / sidebar / share / ...) can translate a
 * `WorkspaceMode` into a concrete URL without importing host-local
 * `@/routes/routeConfig`.  Extended in P11-4h-pass1 with `RouteEntry`
 * + `getRouteByPath` + `extractIdFromPath` so shared workspace-feature
 * hooks (`useWorkspaceRoute`, `resolveWorkspaceRoute`, ...) can answer
 * "which mode/entry matches this pathname?" without importing host.
 *
 * Tenants own the actual route table (B2C ships a full set; on-prem
 * tenants may remove `live-agent`, `document`, `doc-agent`, `widget`,
 * `feed`, or `pricing` depending on their manifest).  The host
 * bootstrap is responsible for calling every `setXxx(...)` below with
 * a tenant-aware resolver BEFORE any shared component renders.
 *
 * Until registered, resolvers return no-op fallbacks to avoid throwing
 * during tests / module-load; production hosts always register at
 * bootstrap so these fallbacks are never observed at runtime.
 * ===================================================================== */

import type { WorkspaceMode } from '@auw/types';

/* -------------------- RouteEntry (shared type) -------------------- */

export interface RouteEntrySEO {
  titleKey?: string;
  descriptionKey?: string;
  hasPublicStructuredData?: boolean;
}

export interface RouteEntry {
  /** URL path prefix (e.g. '/agents'). */
  path: string;
  /** `chatStore.activeMode` value. */
  mode: WorkspaceMode;
  /** Route supports a `:id` suffix (`/agents/abc123`). */
  hasIdParam: boolean;
  /** SEO metadata (title/description i18n keys). */
  seo?: RouteEntrySEO;
}

/* -------------------- DI slots -------------------- */

export type ModeToPathFn = (mode: WorkspaceMode) => string;
export type ModeToPathWithIdFn = (mode: WorkspaceMode, id: string) => string;
export type GetRouteByPathFn = (pathname: string) => RouteEntry | undefined;
export type ExtractIdFromPathFn = (pathname: string) => string | undefined;

let _modeToPath: ModeToPathFn = () => '/';
let _modeToPathWithId: ModeToPathWithIdFn = (mode) => _modeToPath(mode);
let _getRouteByPath: GetRouteByPathFn = () => undefined;
let _extractIdFromPath: ExtractIdFromPathFn = () => undefined;

export function setModeToPath(fn: ModeToPathFn): void {
  _modeToPath = fn;
}

export function setModeToPathWithId(fn: ModeToPathWithIdFn): void {
  _modeToPathWithId = fn;
}

export function setGetRouteByPath(fn: GetRouteByPathFn): void {
  _getRouteByPath = fn;
}

export function setExtractIdFromPath(fn: ExtractIdFromPathFn): void {
  _extractIdFromPath = fn;
}

/* -------------------- public resolvers -------------------- */

/** WorkspaceMode → tenant-resolved URL path. */
export function modeToPath(mode: WorkspaceMode): string {
  return _modeToPath(mode);
}

/** WorkspaceMode + id → tenant-resolved URL path (with `:id` segment). */
export function modeToPathWithId(mode: WorkspaceMode, id: string): string {
  return _modeToPathWithId(mode, id);
}

/** URL pathname → matching RouteEntry (first prefix match). */
export function getRouteByPath(pathname: string): RouteEntry | undefined {
  return _getRouteByPath(pathname);
}

/** URL pathname → trailing `:id` parameter when the route supports one. */
export function extractIdFromPath(pathname: string): string | undefined {
  return _extractIdFromPath(pathname);
}
